import { Link } from "react-router-dom";
import {
  KeyIcon, GaugeIcon, RocketLaunchIcon, LightningIcon, ChartLineUpIcon, CoinsIcon,
  StarIcon, CurrencyDollarIcon, TargetIcon, CopySimpleIcon, ProhibitIcon, TrophyIcon,
  UsersThreeIcon, SkullIcon, EyeSlashIcon, ArrowsLeftRightIcon, CpuIcon, LockKeyIcon,
  LockKeyOpenIcon, PlantIcon, ShieldCheckIcon, GlobeIcon, FireIcon, DiamondIcon, EyeIcon,
} from "@phosphor-icons/react";
import { useSeo } from "../lib/useSeo";

const GREEN = "#00E599";
const GOLD = "#F0C000";
const PURPLE = "#A78BFA";
const RED = "#FF3366";
const AMBER = "#F0A020";
const CYAN = "#22D3EE";

const KIND = {
  token: { label: "TOKEN BEHAVIOR", color: GREEN, note: "what the coin is doing" },
  wallet: { label: "WALLET / SMART MONEY", color: GOLD, note: "what proven wallets are doing" },
  community: { label: "COMMUNITY", color: PURPLE, note: "what the crowd is doing" },
  mod: { label: "COMMUNITY MODERATION", color: RED, note: "what users are flagging" },
};

const SECTIONS = [
  {
    id: "token",
    heading: "📈 Token Signals",
    sub: "These describe what the COIN itself is doing — price, volume, age, liquidity. No people involved.",
    color: GREEN,
    items: [
      { icon: GaugeIcon, color: GREEN, title: "Degen Score (0–100)", kind: "token",
        desc: "A transparent read on a coin built from volume conviction, liquidity safety, buy pressure, survival and credibility. Higher = healthier setup. It rates the TOKEN, not who's buying it." },
      { icon: RocketLaunchIcon, color: AMBER, title: "Running / Heating / Waking Up", kind: "token",
        desc: "Momentum states. The coin's volume is spiking above its hourly average with net buy pressure. 'Running' is the hottest, 'Waking up' is early. This is the CHART moving — not a signal that your wallets bought." },
      { icon: LightningIcon, color: GREEN, title: "Fast Mover", kind: "token",
        desc: "A violent 1-hour price move (pump 🚀 or dump 🔻) on a low-cap coin. Pure price action — the token's lifecycle happening fast." },
      { icon: ChartLineUpIcon, color: GREEN, title: "Momentum & Reversal", kind: "token",
        desc: "Technical read of the coin's trend and whether it's likely continuing or turning. Chart-derived, about the token." },
      { icon: CoinsIcon, color: AMBER, title: "Early Gamble", kind: "token",
        desc: "Very young, low-cap coins surfacing on the radar before the crowd. High risk / high reward — a property of the token, not a buy signal." },
      { icon: RocketLaunchIcon, color: GREEN, title: "Launchpad (bonding curve)", kind: "token",
        desc: "A live pump.fun coin approaching migration (e.g. 80%+ of its bonding curve) with rising buy velocity. About the token's launch progress." },
      { icon: StarIcon, color: AMBER, title: "Alpha Dev Launch", kind: "token",
        desc: "A brand-new coin minted by a creator on your Alpha Devs list. It flags WHO made the token (a dev you rate), before it hits the radar." },
    ],
  },
  {
    id: "discovery",
    heading: "🔎 Discovery, Quality & Safety",
    sub: "These grade the coin's setup and safety — how strong the demand is, whether it's a real product, and the rug vectors. Still all about the TOKEN.",
    color: CYAN,
    items: [
      { icon: EyeIcon, color: GOLD, title: "Conviction (0–100)", kind: "token",
        desc: "Credibility-blind score — pure Volume + Survival + Buy Pressure, ignoring website/socials. Catches real runners even when the token info looks weak. 75+ = 👀 ANSEM-tier. Sort & filter the Radar by it." },
      { icon: CpuIcon, color: CYAN, title: "Tech Score & Tech Tokens", kind: "token",
        desc: "How much a coin looks like a real product, not a pure meme: live website + real socials + utility keywords + survival. Tech plays tend to grind up. The Tech Tokens board ranks them; the Recap shows how past calls ran." },
      { icon: GlobeIcon, color: GREEN, title: "Website Health (Live / Parked / Dead)", kind: "token",
        desc: "We live-fetch each tech token's site: 🌐 Live = real content, 🅿️ Parked = a 'for sale' page, 💀 Dead = didn't load. A tech claim with a parked or dead site is a red flag." },
      { icon: TrophyIcon, color: GOLD, title: "Runner Score & Could Be Today's Runner", kind: "token",
        desc: "How closely a fresh, organic survivor matches the daily-runner pattern forming early — momentum + conviction + survival + clean structure. 72+ pings the '🏆 Could Be Today's Runner' alert." },
      { icon: FireIcon, color: AMBER, title: "Theme / New Meta", kind: "token",
        desc: "A cluster of fresh tokens sharing a keyword in their names — a new meta forming. We surface it so you can hunt the cleanest survivor of that theme early." },
      { icon: LockKeyIcon, color: GREEN, title: "🔒 LP Locked / Burned", kind: "token",
        desc: "Liquidity is locked or burned, so the dev can't pull it — one less rug vector. Filter the Radar to LP-safe coins only. (Still DYOR on holders & authorities.)" },
      { icon: LockKeyOpenIcon, color: RED, title: "🔓 LP Unlocked — BEWARE", kind: "token",
        desc: "Liquidity isn't locked/burned; the dev can pull it at any time. Real demand can still rug fast — take profits quick." },
      { icon: CopySimpleIcon, color: RED, title: "🧛 Vamp / OG (copycat)", kind: "token",
        desc: "🧛 Vamp = a newer token copying an older, notable ticker. OG = the original. When several tokens share a ticker we rank them and mark the likely-real leader vs copycats." },
      { icon: PlantIcon, color: GREEN, title: "🌱 Organic vs 💸 Boosted", kind: "token",
        desc: "Organic = surfaced via trending / high-volume pools (real demand). Boosted = appeared via a paid DexScreener promotion. Verify a boosted coin's demand is real." },
      { icon: DiamondIcon, color: CYAN, title: "💎 Elite / 🏆 Quality / 🛡️ Survivor / 🐋 Whale Magnet", kind: "token",
        desc: "Free market-quality gates: Survivor lasted past the rug window, Whale Magnet is drawing whale-sized buys, Quality is both at once (the killer shortlist), and Elite passes all 5 liquidity/volume/mcap/age/txn gates." },
    ],
  },
  {
    id: "wallet",
    heading: "💰 Smart-Money & Wallet Signals",
    sub: "These describe what PEOPLE — the proven wallets you track — are doing. The coin may be quiet; this is about the money moving into it.",
    color: GOLD,
    items: [
      { icon: CurrencyDollarIcon, color: GOLD, title: "Milly's Call", kind: "wallet",
        desc: "A tracked wallet throwing REAL size at a token — a single whale buy or stacking buys (e.g. 10 → 10 → 20 SOL) crossing your SOL threshold within 6h. Cuts through the 0.1-SOL noise. This is a WALLET action." },
      { icon: RocketLaunchIcon, color: GOLD, title: "🏃💰 Milly Runner Call", kind: "wallet", highlight: true,
        desc: "The convergence super-signal: a coin that is RUNNING (token behavior) AND getting a big Milly buy from a tracked wallet (wallet behavior) at the same time. Smart money loading up on a coin that's already moving — the strongest tell yet." },
      { icon: TargetIcon, color: PURPLE, title: "Cluster Buy", kind: "community",
        desc: "Multiple different tracked wallets buying the SAME token within 6h — a convergence of PEOPLE, not the chart. High-conviction 'something's brewing' signal. Re-pings as more wallets pile in." },
      { icon: CopySimpleIcon, color: GOLD, title: "Copy-Wallet Buy", kind: "wallet",
        desc: "A BUY from a high-performing wallet (PnL score ≥ 60, your 🔥 COPY wallets). About a proven person's action, so you can copy-trade them." },
      { icon: UsersThreeIcon, color: GOLD, title: "Smart Money Trade", kind: "wallet",
        desc: "Any buy or sell from the wallets you track on the Smart Money page. Raw wallet activity — who's moving, not what the chart is doing." },
      { icon: TrophyIcon, color: GOLD, title: "Proven Wallet (PnL score)", kind: "wallet",
        desc: "A wallet with a strong track record (PnL score ≥ 60 shows a 🔥). It rates the PERSON behind the trade, not the token." },
      { icon: ProhibitIcon, color: RED, title: "Blacklisted Dev Buy", kind: "wallet",
        desc: "A tracked wallet bought a coin made by a dev YOU flagged as a rugger. A warning about the people involved, not the price." },
    ],
  },
  {
    id: "mod",
    heading: "🛡️ Community Moderation",
    sub: "These are crowd-sourced flags. They reflect what USERS think about a token's safety — not on-chain price behavior.",
    color: RED,
    items: [
      { icon: SkullIcon, color: RED, title: "Trash / Scam Flag", kind: "mod",
        desc: "Any user can mark a token as trash/scam. It hides from your own Radar and Command Center immediately. A personal, human judgment call." },
      { icon: EyeSlashIcon, color: RED, title: "Community Auto-Suppression", kind: "mod",
        desc: "When 3+ different users flag the same token, it's auto-hidden from everyone's live feeds (shown as an 'auto-hidden' note). Crowd consensus, not chart data." },
      { icon: SkullIcon, color: RED, title: "Graveyard", kind: "mod",
        desc: "The admin log of community-flagged scams. A record of what the crowd has buried — moderation history, not market data." },
    ],
  },
];

const KindTag = ({ kind }) => {
  const k = KIND[kind];
  return (
    <span className="font-mono text-[9px] px-1.5 py-0.5 tracking-wider shrink-0" style={{ background: `${k.color}18`, border: `1px solid ${k.color}66`, color: k.color }}>
      {k.label}
    </span>
  );
};

const SignalCard = ({ item }) => {
  const Icon = item.icon;
  return (
    <div
      data-testid={`key-card-${item.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
      className={`flex gap-3.5 p-4 border bg-card/40 transition-colors hover:bg-white/[0.03] ${item.highlight ? "milly-glow" : ""}`}
      style={{ borderColor: item.highlight ? `${item.color}88` : "rgba(255,255,255,0.08)", borderLeft: `3px solid ${item.color}` }}
    >
      <div className="w-9 h-9 flex items-center justify-center border shrink-0 mt-0.5" style={{ borderColor: `${item.color}55`, color: item.color }}>
        <Icon size={18} weight="bold" />
      </div>
      <div className="min-w-0 space-y-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-heading font-bold text-sm" style={item.highlight ? { color: item.color } : {}}>{item.title}</span>
          <KindTag kind={item.kind} />
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
      </div>
    </div>
  );
};

export default function Key() {
  useSeo({
    title: "The Key — Signal & Community Glossary | AlphaApeing",
    description: "What every AlphaApeing signal means — and the crucial difference between what the TOKEN is doing vs what the COMMUNITY and smart-money wallets are doing.",
  });

  return (
    <div className="max-w-[980px] mx-auto px-4 sm:px-6 py-6 space-y-8 animate-fade-up" data-testid="key-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center bg-neon text-black">
            <KeyIcon size={24} weight="bold" />
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">THE KEY</h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          Every call on AlphaApeing, decoded. Some signals describe <span className="text-neon font-semibold">what the coin is doing</span>,
          others describe <span style={{ color: GOLD }} className="font-semibold">what proven wallets are doing</span>, and others are
          <span style={{ color: RED }} className="font-semibold"> community flags</span>. Knowing the difference is everything.
        </p>
      </div>

      {/* the key distinction callout */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="key-legend">
        {["token", "wallet", "mod"].map((kk) => {
          const k = KIND[kk];
          return (
            <div key={kk} className="p-4 border bg-card/40" style={{ borderColor: `${k.color}44`, borderTop: `3px solid ${k.color}` }}>
              <div className="font-mono text-[10px] tracking-wider font-bold" style={{ color: k.color }}>{k.label}</div>
              <div className="text-xs text-muted-foreground mt-1">= {k.note}</div>
            </div>
          );
        })}
      </div>

      <div className="flex items-start gap-3 p-4 border border-white/10 bg-black/30">
        <ArrowsLeftRightIcon size={20} className="text-neon shrink-0 mt-0.5" weight="bold" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          <span className="text-white font-semibold">The big one to remember:</span> a <b>Cluster Buy</b> or <b>Milly's Call</b> means <i>people</i> are
          moving — the chart itself might still be flat. A <b>Running</b> or <b>Fast Mover</b> means the <i>token's</i> price/volume is moving —
          regardless of whether your wallets touched it. When BOTH line up on one coin, that's a <span style={{ color: GOLD }} className="font-semibold">🏃💰 Milly Runner Call</span>.
        </p>
      </div>

      {SECTIONS.map((sec) => (
        <section key={sec.id} data-testid={`key-section-${sec.id}`} className="space-y-3">
          <div className="flex items-center gap-3 border-b border-white/10 pb-2">
            <h2 className="font-heading font-black text-xl tracking-tight" style={{ color: sec.color }}>{sec.heading}</h2>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed max-w-2xl -mt-1">{sec.sub}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sec.items.map((it) => <SignalCard key={it.title} item={it} />)}
          </div>
        </section>
      ))}

      <div className="text-center text-xs font-mono text-muted-foreground border-t border-white/5 pt-6">
        Research tool, not financial advice. Signals are heuristics — always DYOR. ·{" "}
        <Link to="/methodology" className="text-neon hover:underline">Read the full methodology →</Link>
      </div>
    </div>
  );
}
